import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Minus, Plus, Check } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWeight, setSelectedWeight] = useState(100);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${slug}`);
        setProduct(response.data);
        if (response.data.weights && response.data.weights.length > 0) {
          setSelectedWeight(response.data.weights[0]);
        }
      } catch (error) {
        console.error("Failed to fetch product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, selectedWeight, quantity);
      toast.success(`${product.name} (${selectedWeight}g x ${quantity}) sepete eklendi`);
    }
  };

  const calculatePrice = () => {
    if (!product) return 0;
    const multiplier = selectedWeight === 250 ? 2.2 : selectedWeight === 500 ? 4 : 1;
    return product.price * multiplier * quantity;
  };

  if (loading) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="heading-lg text-[#0D3B2E] mb-4">Ürün Bulunamadı</h1>
          <Link to="/urunler" className="btn-primary">
            Ürünlere Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="bg-[#F9F7F2] py-4 border-b border-[#E5E0D8]">
        <div className="section-container">
          <Link
            to="/urunler"
            className="inline-flex items-center gap-2 text-[#5D4037] hover:text-[#0D3B2E] transition-colors"
            data-testid="back-to-products"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="caption-text">Ürünlere Dön</span>
          </Link>
        </div>
      </div>

      {/* Product Detail */}
      <section className="section-spacing bg-[#F9F7F2]">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full aspect-square object-cover"
                data-testid="product-image"
              />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col"
            >
              <span className="caption-text text-[#C5A059] mb-2">
                {product.roast_level}
              </span>
              <h1
                className="heading-xl text-[#0D3B2E] mb-4"
                data-testid="product-name"
              >
                {product.name}
              </h1>
              <p className="body-text text-[#5D4037] mb-8">
                {product.description}
              </p>

              {/* Aroma Notes */}
              <div className="mb-8">
                <h3 className="caption-text text-[#5D4037]/70 mb-3">
                  Aroma Notları
                </h3>
                <div className="flex flex-wrap gap-2">
                  {product.aroma_notes.map((note, i) => (
                    <span
                      key={i}
                      className="px-4 py-2 bg-white border border-[#E5E0D8] text-[#5D4037] text-sm"
                    >
                      {note}
                    </span>
                  ))}
                </div>
              </div>

              {/* Taste Profile */}
              <div className="mb-8">
                <h3 className="caption-text text-[#5D4037]/70 mb-3">
                  Tat Profili
                </h3>
                <p className="body-text text-[#0D3B2E]">{product.taste_profile}</p>
              </div>

              {/* Usage Tips */}
              <div className="mb-8">
                <h3 className="caption-text text-[#5D4037]/70 mb-3">
                  Kullanım Önerisi
                </h3>
                <p className="body-text text-[#5D4037] text-sm">
                  {product.usage_tips}
                </p>
              </div>

              {/* Weight Selection */}
              <div className="mb-8">
                <h3 className="caption-text text-[#5D4037]/70 mb-3">
                  Gramaj Seçimi
                </h3>
                <div className="flex gap-3">
                  {product.weights.map((weight) => (
                    <button
                      key={weight}
                      onClick={() => setSelectedWeight(weight)}
                      className={`px-6 py-3 border transition-all ${
                        selectedWeight === weight
                          ? "border-[#0D3B2E] bg-[#0D3B2E] text-white"
                          : "border-[#E5E0D8] text-[#5D4037] hover:border-[#0D3B2E]"
                      }`}
                      data-testid={`weight-${weight}`}
                    >
                      {weight}g
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-8">
                <h3 className="caption-text text-[#5D4037]/70 mb-3">Adet</h3>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 border border-[#E5E0D8] flex items-center justify-center hover:border-[#0D3B2E] transition-colors"
                    data-testid="decrease-quantity"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span
                    className="font-display text-2xl text-[#0D3B2E] w-12 text-center"
                    data-testid="quantity-display"
                  >
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 border border-[#E5E0D8] flex items-center justify-center hover:border-[#0D3B2E] transition-colors"
                    data-testid="increase-quantity"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Price & Add to Cart */}
              <div className="mt-auto pt-8 border-t border-[#E5E0D8]">
                <div className="flex items-center justify-between mb-6">
                  <span className="caption-text text-[#5D4037]/70">Toplam</span>
                  <span
                    className="font-display text-3xl text-[#0D3B2E]"
                    data-testid="total-price"
                  >
                    ₺{calculatePrice().toFixed(2)}
                  </span>
                </div>
                <button
                  onClick={handleAddToCart}
                  className="btn-gold w-full flex items-center justify-center gap-2"
                  data-testid="add-to-cart-button"
                >
                  <Check className="w-5 h-5" />
                  Sepete Ekle
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
